import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { Logo } from '../components/brand/Logo';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';

export const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  const content = `QUANTUMCUE PRIVACY POLICY
Last Updated: March 15, 2026

This Privacy Policy explains how QuantumCue, Inc. (“QuantumCue,” “we,” “us,” or “our”), a Delaware corporation with its principal place of business in New York, New York, collects, uses, discloses, and otherwise processes personal information.
This includes personal information collected in connection with your access to or use of QuantumCue’s platform, products, software, APIs, and services (collectively, the “Services”).
By using the Services, entering into an agreement with QuantumCue, or otherwise providing personal information to us, you agree to this Privacy Policy and consent to the collection, use, processing, and transfer of your personal information as described herein.

1. WHAT PERSONAL INFORMATION DO WE COLLECT?
“Personal information” means information that identifies, relates to, describes, or can reasonably be linked to an individual, as defined under applicable law.
We collect personal information as necessary to provide the Services and operate our business.

1.1 Information You Provide
We generally collect personal information directly from you. For example:
When you register for an account:
• Name
• Username
• Email address
• Organization name and role
• Industry or affiliation

When you use paid Services:
• Billing and payment information (processed via third-party payment providers; we do not store full payment card details)

When you communicate with us:
• Support requests
• Feedback
• Emails or other communications

When you connect third-party services:
• Account identifiers (e.g., GitHub username)
• Public repository or integration metadata (if authorized)

In other situations:
• Any information you provide with your consent or as permitted by law

1.2 Platform, Workflow, and Usage Data
When you use the Services, we may collect:
• Project names and metadata
• Workflow configurations
• Job submissions and execution metadata
• API calls and usage metrics
• Logs, diagnostics, and performance data
• Feature usage and interaction patterns

Important: QuantumCue processes this data solely to provide and improve the Services. We do not claim ownership of Customer Data or results.

1.3 Automatically Collected Information
We may collect information automatically using cookies and similar technologies, including:
• IP address
• Device identifiers
• Browser type and version
• Operating system
• Access timestamps
• Usage statistics
• Platform performance data

We use this information to:
• Authenticate users
• Monitor usage
• Enforce agreements
• Detect fraud and security risks

1.4 Customer-Provided Processing (Important Distinction)
This Privacy Policy does not apply where QuantumCue processes personal data solely on behalf of a customer under a separate contractual relationship (e.g., enterprise use cases). In such cases, processing is governed by the applicable agreement (such as a Data Processing Addendum).

2. HOW DO WE USE PERSONAL INFORMATION?
We use personal information:

2.1 For Legitimate Business Interests
Including to:
• Provide and operate the Services
• Execute workflows and process job requests
• Manage accounts and authenticate users
• Process payments and billing
• Respond to inquiries and provide support
• Maintain, improve, and optimize the platform
• Analyze usage and develop new features
• Detect and prevent fraud or abuse
• Protect QuantumCue, its users, and systems
• Enforce agreements and policies

2.2 To Perform a Contract
To fulfill our obligations under:
• Terms of Service
• Enterprise agreements
• Orders or subscriptions

2.3 To Comply with Legal Obligations
Including:
• Regulatory compliance
• Law enforcement requests
• Legal claims or proceedings

2.4 With Your Consent
Where required, including:
• Marketing communications
• Participation in recorded sessions
• Optional integrations
You may withdraw consent at any time by contacting us.

3. SHARING OF INFORMATION
We do not sell or license your personal information.
We may share personal information as follows:

3.1 Service Providers
With vendors and service providers that support our operations, including:
• Cloud infrastructure providers
• Hosting and data processing services
• Payment processors
• Analytics and monitoring tools
• Customer support systems

3.2 Integrated Compute Providers
To the extent necessary to:
• Execute jobs
• Process workflows
• Route compute requests
These providers operate independently and are subject to their own privacy policies.

3.3 Business Transactions
In connection with:
• Mergers
• Acquisitions
• Financings
• Asset sales

3.4 Legal Requirements
Where required by law, regulation, or legal process, or to:
• Protect rights or safety
• Prevent fraud or abuse

3.5 With Consent
In any other circumstance where you have provided consent.

4. INTERNATIONAL TRANSFERS
QuantumCue operates from the United States (New York).
Your personal information may be transferred to, stored, and processed in the United States or other jurisdictions where our service providers operate.
By using the Services, you consent to such transfers.
Where required by applicable law, we implement appropriate safeguards for cross-border data transfers.

5. RETENTION
We retain personal information:
• As long as necessary to provide the Services
• To fulfill contractual and legal obligations
• For legitimate business purposes
We may retain certain information for longer periods where permitted by law (e.g., backups, compliance, dispute resolution).

6. YOUR RIGHTS
Depending on your jurisdiction, you may have rights including:
• Access to your personal information
• Correction of inaccurate data
• Deletion of your data
• Restriction or objection to processing
• Data portability
• Withdrawal of consent
To exercise your rights, contact us at: privacy@quantumcue.io
We may request verification of identity before fulfilling requests.

7. SECURITY
Personal information is stored on QuantumCue systems or those of our service providers and is accessible only to authorized personnel.
We maintain technical, administrative, and physical safeguards, including:
• Encryption
• Access controls
• Network security measures
• Monitoring and logging
However: No system is completely secure.

8. CHILDREN’S PRIVACY
The Services are not intended for individuals under 18.
We do not knowingly collect personal information from minors. If we become aware that such data has been collected, we will delete it.

9. CHANGES TO THIS POLICY
We may update this Privacy Policy from time to time.
If changes are material, we will provide notice via:
• Website posting
• Platform notification
• Email (if applicable)
Your continued use of the Services constitutes acceptance of the updated Policy.

10. CONTACT
If you have questions or concerns about this Privacy Policy or our data practices, please contact:
QuantumCue, Inc.
privacy@quantumcue.io
You may also have the right to file a complaint with a data protection authority in your jurisdiction.`;

  const renderParagraph = (text: string, index: number) => {
    const trimmed = text.trim();
    if (!trimmed) return <div key={index} className="h-4" />;

    // Check if it's a main heading (all caps, length > 3)
    const isMainHeading = trimmed === trimmed.toUpperCase() && trimmed.length > 3 && !trimmed.startsWith('IMPORTANT:') && !trimmed.startsWith('•');
    
    // Check if it's a section heading (e.g., 1. WHAT... or 1.1 Information...)
    const isSectionHeading = /^\d+(\.\d+)*\s/.test(trimmed);

    if (isMainHeading) {
      return (
        <h2 key={index} className="text-xl font-bold text-brand-50 dark:text-brand-50 mt-8 mb-4 border-b border-brand-50/20 pb-2">
          {trimmed}
        </h2>
      );
    }

    if (isSectionHeading) {
      return (
        <h3 key={index} className="text-lg font-semibold text-grey-900 dark:text-text-primary mt-6 mb-3">
          {trimmed}
        </h3>
      );
    }

    if (trimmed.startsWith('•')) {
      return (
        <li key={index} className="ml-6 mb-2 text-grey-700 dark:text-text-secondary list-none flex items-start gap-2">
          <span className="text-brand-50 font-bold">•</span>
          <span>{trimmed.substring(1).trim()}</span>
        </li>
      );
    }

    return (
      <p key={index} className="mb-4 text-grey-700 dark:text-text-secondary leading-relaxed text-base">
        {trimmed}
      </p>
    );
  };

  return (
    <div className="min-h-screen bg-grey-50 dark:bg-background flex flex-col items-center p-4 sm:p-6 lg:p-8">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 dark:bg-navy-700/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 dark:bg-cyan-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-4xl">
        {/* Top Navigation Bar */}
        <div className="flex items-center justify-between mb-8 w-full">
          <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/login')}>
            <Logo variant="full-with-tagline" size="md" />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-grey-600 dark:text-text-secondary hover:text-brand-50 dark:hover:text-brand-50"
          >
            <ArrowLeft size={16} />
            <span>Back</span>
          </Button>
        </div>

        {/* Content Card */}
        <Card className="w-full p-6 sm:p-10 shadow-xl border-t-4 border-t-brand-50">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-brand-50/10 rounded-xl text-brand-50">
              <Shield size={32} />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-grey-900 dark:text-text-primary">
                Privacy Policy
              </h1>
              <p className="text-sm text-grey-500 dark:text-text-tertiary">
                Last Updated: March 15, 2026
              </p>
            </div>
          </div>

          <div className="prose dark:prose-invert max-w-none">
            {content.split('\n').map((paragraph, index) => renderParagraph(paragraph, index))}
          </div>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-grey-400 dark:text-text-tertiary">
          &copy; {new Date().getFullYear()} QuantumCue, Inc. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
